import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { signInWithLoginKey } from "@/lib/host/auth-client";
import { ShellPending } from "@/components/app/shell/shell-pending";
import { orpc } from "@/lib/orpc";
import type { Me } from "@alm/api-contracts";
import { portalHomeFor } from "@alm/domain";
import { needsFirstRunSetup } from "@/lib/session/first-run";
import { beginSession } from "@/lib/session/session";
import { AuthenticatedApp } from "@/routes/-components/authenticated-app";

/** Client-rendered: the session cookie is in the browser and the API is another origin. An
 * anonymous caller gets `me: null`, not a thrown redirect, which is not applied while a client-only
 * tree hydrates; the layout navigates from an effect. */
export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async ({
    context,
    location,
  }): Promise<{ me: Me | null; keyLanding: string | null }> => {
    // A `?key=` is the one-time key in Sarah's handoff text. It is spent before the session is
    // read, since the phone opening the link is signed out. A bad key is ignored.
    const key = new URLSearchParams(location.searchStr).get("key");
    // The server reads the landing off the row the key was issued against, so the URL that carried
    // the key does not decide it.
    let keyLanding: string | null = null;
    if (key) {
      const signedIn = await signInWithLoginKey(key).catch(() => null);
      if (signedIn) {
        // Whoever this phone held before, nothing fetched as them is shown to the key's person.
        beginSession(context.queryClient);
        keyLanding = safeLanding(signedIn.next);
      }
    }
    try {
      // A stale cached answer is asked again behind the page, so a changed role re-runs every guard
      // (lib/session/session.ts).
      const me = await context.queryClient.ensureQueryData({
        ...orpc.auth.me.queryOptions(),
        revalidateIfStale: true,
      });
      return { me, keyLanding };
    } catch {
      return { me: null, keyLanding };
    }
  },
  component: AuthenticatedLayout,
  // Client-rendered, so this is the first paint of every signed-in page: the frame, not a bare
  // page.
  pendingComponent: ShellPending,
});

/** A path on this application and nothing else, as the API's own check has it. */
function safeLanding(next: string | undefined): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}

/** The same address without the one-time key. */
function withoutKey(href: string): string {
  const [path = "/", rest = ""] = href.split("?");
  const [query = "", hash] = rest.split("#");
  const params = new URLSearchParams(query);
  params.delete("key");
  const search = params.toString();
  return `${path}${search ? `?${search}` : ""}${hash === undefined ? "" : `#${hash}`}`;
}

function AuthenticatedLayout() {
  const { me, keyLanding } = Route.useRouteContext();
  const router = useRouter();
  const navigate = useNavigate();
  // Only what the guard decides on, so every other change of address does not redraw the shell.
  const carriesKey = useLocation({
    select: (location) => new URLSearchParams(location.searchStr).has("key"),
  });
  const alreadyOnLogin = useLocation({
    select: (location) => location.pathname.startsWith("/login"),
  });
  // Captured once, or /login would nest inside its own `next` while the redirect is in flight.
  // Without the key, which is spent.
  const [returnTo] = useState(() => withoutKey(router.state.location.href));
  const anonymous = !me;
  // A temporary password is one somebody else may know, so it reaches exactly one page until a
  // password is chosen.
  const mustChoose = Boolean(me?.user.mustResetPassword);
  // Applicants, family, partners and physicians have their own portal; this shell is never shown to
  // them.
  const portalHome = portalHomeFor(me?.user.role);
  // Public sign-ups finish the guided first run on a page outside the shell before it opens.
  const onboardingFirst = Boolean(
    me && me.onboarding && !me.onboarding.completed && !me.subject.platformAdmin,
  );
  // A new organization's administrator creates its first building first, outside this shell.
  const setupFirst = Boolean(me && !onboardingFirst && needsFirstRunSetup(me));
  // `useLocation` updates when a navigation starts, so leaving this tree looks like a refusal. Act
  // only while `/_authenticated` is matched and the router has settled.
  const guardApplies = useRouterState({
    select: (state) =>
      state.status !== "pending" &&
      state.matches.some((match) => match.routeId === "/_authenticated"),
  });
  // In an effect: navigating during render updates the router before this component has mounted.
  useEffect(() => {
    if (!guardApplies) return;
    // The key comes off the address once tried: left on, each reload posts a spent key against the
    // building's shared sign-in limiter and the credential stays in the phone's history.
    if (carriesKey && !anonymous) {
      void navigate({ href: keyLanding ?? withoutKey(router.state.location.href), replace: true });
      return;
    }
    if (anonymous && !alreadyOnLogin) {
      void navigate({ to: "/login", search: { next: returnTo }, replace: true });
      return;
    }
    if (mustChoose) {
      void navigate({ to: "/set-new-password", replace: true });
      return;
    }
    if (portalHome) {
      void navigate({ href: portalHome, replace: true });
      return;
    }
    if (onboardingFirst) {
      void navigate({ to: "/onboarding", replace: true });
      return;
    }
    if (setupFirst) void navigate({ to: "/setup", replace: true });
  }, [
    onboardingFirst,
    anonymous,
    alreadyOnLogin,
    mustChoose,
    portalHome,
    setupFirst,
    navigate,
    returnTo,
    guardApplies,
    carriesKey,
    keyLanding,
    router,
  ]);
  if (anonymous || mustChoose || portalHome || onboardingFirst || setupFirst) return null;
  return <AuthenticatedApp />;
}
